import { useState, useEffect, useCallback } from 'react';
import AccountLayout from '@/components/feature/AccountLayout';
import { supabase } from '@/lib/supabase';

interface Notification {
  id: string;
  type: string;
  title: string;
  content: string | null;
  is_read: boolean;
  created_at: string;
}

const typeStyle: Record<string, { icon: string; cls: string }> = {
  deposit_withdraw: { icon: 'ri-bank-card-line', cls: 'text-accent-400 bg-accent-500/15' },
  price: { icon: 'ri-line-chart-line', cls: 'text-primary-400 bg-primary-500/15' },
  trade: { icon: 'ri-exchange-line', cls: 'text-emerald-400 bg-emerald-500/15' },
  system: { icon: 'ri-settings-3-line', cls: 'text-foreground-400 bg-background-200/60' },
  promo: { icon: 'ri-gift-line', cls: 'text-accent-400 bg-accent-500/15' },
};

export default function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [marking, setMarking] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) return;
      const { data: notifRaw, error: fetchError } = await supabase.rpc('get_user_notifications_json', { p_user_id: userId });
      if (fetchError) throw fetchError;
      const data = Array.isArray(notifRaw) ? notifRaw : JSON.parse((notifRaw as string) || '[]');
      setNotifications(data.slice(0, 50) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải thông báo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAllRead = async () => {
    setMarking(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) return;
      await supabase.from('notifications').update({ is_read: true }).eq('user_id', userId).eq('is_read', false);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể cập nhật.');
    } finally {
      setMarking(false);
    }
  };

  const markOneRead = async (id: string) => {
    try {
      await supabase.from('notifications').update({ is_read: true }).eq('id', id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch (err) {
      console.error(err);
    }
  };

  const unread = notifications.filter((n) => !n.is_read).length;

  const styleFor = (type: string) => typeStyle[type] || typeStyle.system;

  return (
    <AccountLayout>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-foreground-950">Trung tâm thông báo</h1>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              disabled={marking}
              className="text-sm font-semibold text-primary-400 hover:text-primary-300 cursor-pointer whitespace-nowrap"
            >
              Đánh dấu đã đọc tất cả
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-base"></i>
            <p className="text-sm text-rose-400">{error}</p>
            <button onClick={fetchNotifications} className="ml-auto text-sm font-semibold text-rose-400 cursor-pointer whitespace-nowrap">
              Thử lại
            </button>
          </div>
        )}

        {loading ? (
          <p className="py-10 text-center text-sm text-foreground-600">Đang tải...</p>
        ) : notifications.length === 0 ? (
          <div className="rounded-xl border border-background-200/60 bg-background-100 py-12 text-center">
            <i className="ri-notification-off-line text-3xl block mb-2 text-foreground-500"></i>
            <p className="text-sm text-foreground-600">Chưa có thông báo nào</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((n) => {
              const s = styleFor(n.type);
              return (
                <div
                  key={n.id}
                  onClick={() => !n.is_read && markOneRead(n.id)}
                  className={`rounded-xl border p-4 flex items-start gap-3 cursor-pointer transition-colors ${
                    n.is_read ? 'border-background-200/60 bg-background-100' : 'border-primary-500/30 bg-primary-500/5'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${s.cls}`}>
                    <i className={`${s.icon} text-lg`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold text-foreground-950 ${n.is_read ? '' : 'text-primary-300'}`}>{n.title}</p>
                      {!n.is_read && <span className="w-2 h-2 rounded-full bg-primary-400"></span>}
                    </div>
                    {n.content && <p className="text-sm text-foreground-600 mt-1">{n.content}</p>}
                    <p className="text-xs text-foreground-600 mt-2">
                      {new Date(n.created_at).toLocaleString('vi-VN')}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AccountLayout>
  );
}